import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, IndianRupee, ClipboardCheck, Camera, ChevronDown } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";
import { WhatsAppIcon, GoldDiamond } from "@/components/icons";
import { waLink } from "@/config/site";
import { IMAGES } from "@/config/images";
import { track } from "@/lib/analytics";

const CHIPS = [
  { Icon: IndianRupee, label: "Rent received", dot: "bg-status", className: "left-0 top-[16%] -translate-x-[12%]", delay: 1.25, testid: "hero-chip-rent" },
  { Icon: ClipboardCheck, label: "Inspection completed", dot: "bg-gold-400", className: "right-0 top-[2%] translate-x-[8%]", delay: 1.5, testid: "hero-chip-inspection" },
  { Icon: Camera, label: "Photos uploaded", dot: "bg-gold-400", className: "right-[4%] bottom-[20%]", delay: 1.75, testid: "hero-chip-photos" },
];

const Chip = ({ chip, style }) => (
  <motion.div
    style={style}
    initial={{ opacity: 0, y: 20, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay: chip.delay, duration: 0.7, ease: EASE }}
    className={`absolute z-20 ${chip.className}`}
    data-testid={chip.testid}
  >
    <motion.div
      animate={{ y: [0, -9, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: chip.delay }}
      className="glass-chip flex items-center gap-2.5 px-4 py-3"
    >
      <span className={`h-2 w-2 rounded-full ${chip.dot}`} aria-hidden="true" />
      <chip.Icon className="h-4 w-4 text-white/80" />
      <span className="whitespace-nowrap font-display text-xs font-bold text-white">{chip.label}</span>
    </motion.div>
  </motion.div>
);

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const frameX = useTransform(sx, [-0.5, 0.5], [-9, 9]);
  const frameY = useTransform(sy, [-0.5, 0.5], [-7, 7]);
  const chipX = useTransform(sx, [-0.5, 0.5], [16, -16]);
  const chipY = useTransform(sy, [-0.5, 0.5], [11, -11]);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen items-center overflow-hidden bg-navy-900 pb-20 pt-32 sm:pt-36"
      data-testid="hero-section"
    >
      {/* Atmosphere: gold glow + faint architectural grid */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 88% 6%, rgba(201,162,39,0.16), transparent 62%), radial-gradient(ellipse 45% 40% at -5% 100%, rgba(201,162,39,0.08), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "76px 76px",
        }}
        aria-hidden="true"
      />

      <div className="container-site relative grid items-center gap-16 lg:grid-cols-[1.05fr_0.95fr]">
        {/* Copy */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="flex items-center gap-4"
          >
            <span className="gold-rule" aria-hidden="true" />
            <span className="eyebrow">NRI Property Services · Hyderabad</span>
          </motion.div>

          <h1 className="mt-7 font-display text-[13vw] font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[4.6rem]">
            <MaskedLine delay={0.25}>Your Property in</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-gold-400">Hyderabad.</span>
            </MaskedLine>
            <MaskedLine delay={0.51}>Our Presence</MaskedLine>
            <MaskedLine delay={0.64}>on the Ground.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
          >
            Complete property management for NRIs and owners living away from Hyderabad. From tenant
            management and rent collection to inspections, repairs, cleaning, documentation and complete
            property upkeep — we manage your Hyderabad property as if you were here.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              className="btn-gold"
              data-testid="hero-cta-assessment"
              onClick={() => track("assessment_request", { location: "hero" })}
            >
              Get a Free Property Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-light"
              data-testid="hero-cta-whatsapp"
              onClick={() => track("whatsapp_click", { location: "hero" })}
            >
              <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> Talk to Us on WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/45"
            data-testid="hero-trust-line"
          >
            <span>Currently serving Hyderabad</span>
            <GoldDiamond />
            <span>End-to-end property care</span>
            <GoldDiamond />
            <span>Transparent reporting</span>
          </motion.div>
        </div>

        {/* Visual: two clipped frames bridged by live notification chips */}
        <div className="relative mx-auto h-[440px] w-full max-w-[540px] sm:h-[560px]" data-testid="hero-visual">
          <motion.div
            style={{ x: frameX, y: frameY }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="frame-arch absolute right-0 top-0 h-[88%] w-[70%] overflow-hidden border border-white/10"
          >
            <img
              src={IMAGES.heroInspector}
              alt="Property representative inspecting a premium apartment in Hyderabad"
              className="duotone h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent" aria-hidden="true" />
            <span className="absolute bottom-4 left-1/2 w-max max-w-[92%] -translate-x-1/2 rounded-full bg-navy-950/70 px-3 py-1.5 text-center text-[9px] font-bold uppercase tracking-[0.16em] text-gold-300 backdrop-blur-md">
              Our representative · Hyderabad
            </span>
          </motion.div>

          <motion.div
            style={{ x: chipX, y: frameY }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.75, ease: EASE }}
            className="absolute bottom-0 left-0 h-[46%] w-[46%] overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
          >
            <img
              src={IMAGES.heroOwner}
              alt="NRI property owner abroad checking property updates on a laptop"
              className="duotone h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/65 via-transparent to-transparent" aria-hidden="true" />
            <span className="absolute bottom-3 left-1/2 w-max max-w-[92%] -translate-x-1/2 rounded-full bg-navy-950/70 px-2.5 py-1 text-center text-[8px] font-bold uppercase tracking-[0.14em] text-white/80 backdrop-blur-md">
              You · anywhere in the world
            </span>
          </motion.div>

          {CHIPS.map((chip) => (
            <Chip key={chip.testid} chip={chip} style={{ x: chipX, y: chipY }} />
          ))}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/35 sm:flex"
        aria-hidden="true"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
};
